'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import { RotateCw, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { getFileHistory } from './api';
import { formatBytes, formatTimestamp } from './tree';
import type { FileVersion } from './types';

type Phase = 'loading' | 'ready' | 'error' | 'unavailable';

export function FileHistoryDrawer({
  open,
  onOpenChange,
  workspaceId,
  filePath,
  activeVersion,
  onViewVersion,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  filePath: string | null;
  activeVersion?: number;
  onViewVersion: (version: number | undefined) => void;
}) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!open || !filePath) return;
    let cancelled = false;
    setPhase('loading');
    setError(null);
    getFileHistory(workspaceId, filePath).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setVersions(res.data.versions ?? []);
        setPhase('ready');
      } else if (res.notAvailable) {
        setPhase('unavailable');
      } else {
        setPhase('error');
        setError(res.error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, filePath, workspaceId, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);
  const fileName = filePath?.split('/').pop() ?? '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Version history</SheetTitle>
          <SheetDescription className="truncate font-mono text-xs">{fileName}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4" data-testid="history-body">
          {phase === 'loading' && (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Loading history…
            </div>
          )}

          {phase === 'unavailable' && (
            <p className="py-8 text-sm text-muted-foreground">
              Version history is not available yet.
            </p>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-start gap-3 py-8">
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={retry}>
                <RotateCw className="size-3.5" /> Retry
              </Button>
            </div>
          )}

          {phase === 'ready' && versions.length === 0 && (
            <p className="py-8 text-sm text-muted-foreground">No prior versions recorded.</p>
          )}

          {phase === 'ready' && versions.length > 0 && (
            <ol className="space-y-1 py-3">
              {versions.map((v, idx) => {
                const isLatest = idx === 0;
                const isActive = activeVersion === v.version || (activeVersion === undefined && isLatest);
                return (
                  <li
                    key={v.version}
                    data-testid="history-version"
                    className="rounded-lg border border-border/70 bg-muted/10 p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">v{v.version}</span>
                      {isLatest && (
                        <Badge variant="secondary" className="text-[10px]">
                          latest
                        </Badge>
                      )}
                      <Badge variant="outline" className="gap-1 text-[10px]" data-testid="written-by">
                        <User className="size-3" />
                        {v.writtenBy || 'unknown'}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatTimestamp(v.createdAt)} · {formatBytes(v.size)} · {v.contentHash?.slice(0, 10)}
                    </p>
                    <div className="mt-2">
                      <Button
                        variant={isActive ? 'secondary' : 'outline'}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={isActive}
                        onClick={() => onViewVersion(isLatest ? undefined : v.version)}
                      >
                        {isActive ? 'Viewing' : 'View this version'}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
